<template>
  <div>
    <!-- Summary and Refresh -->
    <div class="d-flex align-center mb-2">
      <v-chip color="primary" text-color="white" class="mr-2">
        Total Active Users: {{ users.length }}
      </v-chip>
      <v-btn small color="info" @click="fetchActiveUsers" :loading="loading">
        <v-icon left small>mdi-refresh</v-icon>
        Refresh
      </v-btn>
    </div>
    <v-alert v-if="error" type="error" border="left" colored-border>
      {{ error }}
    </v-alert>
    <div v-else-if="loading" class="d-flex align-center">
      <v-progress-circular
        indeterminate
        color="primary"
        size="28"
        class="mr-2"
      />
      Loading active users...
    </div>
    <v-list v-else dense>
      <v-list-item v-if="users.length === 0">
        <v-list-item-content>
          <v-list-item-title>No active users found.</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-list-item v-for="user in users" :key="user.id">
        <v-list-item-avatar>
          <v-icon>mdi-account</v-icon>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title>
            {{ user.fullname || user.username }}
            <!-- Debug: Show raw roles if mapping fails -->
            <span v-if="!getRoleNames(user.roles).length && user.roles">
              [roles: {{ user.roles }}]
            </span>
            <span
              class="grey--text text--darken-1"
              v-if="getRoleNames(user.roles).length"
            >
              ({{ getRoleNames(user.roles).join(", ") }})
            </span>
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ user.email }}
            <span v-if="user.currentPage" class="ml-2">
              <v-icon small color="primary" class="mr-1">mdi-web</v-icon>
              <span class="caption">Page: {{ user.currentPage }}</span>
            </span>
          </v-list-item-subtitle>
          <v-list-item-subtitle>
            <span v-if="user.loginTime">
              <v-icon small color="green" class="mr-1">mdi-login</v-icon>
              Login: {{ formatDateTime(user.loginTime) }}
            </span>
            <span v-if="user.lastActive" class="ml-2">
              <v-icon small color="blue" class="mr-1">mdi-clock-outline</v-icon>
              Last Active: {{ formatDateTime(user.lastActive) }}
            </span>
          </v-list-item-subtitle>
          <v-list-item-subtitle v-if="user.lastSeen">
            <v-icon small color="blue" class="mr-1">mdi-clock-outline</v-icon>
            Last Seen: {{ formatDateTime(user.lastSeen) }}
          </v-list-item-subtitle>
          <!-- Branches -->
          <v-list-item-subtitle v-if="getBranchNames(user.branches).length">
            <v-icon small color="orange" class="mr-1"
              >mdi-office-building</v-icon
            >
            Branch:
            <span>
              {{ getBranchNames(user.branches).join(", ") }}
            </span>
          </v-list-item-subtitle>
          <!-- Debug: Show raw branches if mapping fails -->
          <v-list-item-subtitle
            v-if="!getBranchNames(user.branches).length && user.branches"
          >
            [branches: {{ user.branches }}]
          </v-list-item-subtitle>
          <!-- Departments (optional, if you want to show) -->
          <v-list-item-subtitle v-if="getDeptNames(user.dept).length">
            <v-icon small color="purple" class="mr-1">mdi-domain</v-icon>
            Department:
            <span v-for="(dept, idx) in getDeptNames(user.dept)" :key="idx">
              {{ dept }}
              <span v-if="idx < getDeptNames(user.dept).length - 1">, </span>
            </span>
          </v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action>
          <v-chip small color="success" text-color="white">Active</v-chip>
        </v-list-item-action>
      </v-list-item>
    </v-list>
  </div>
</template>

<script>
import {
  VAlert,
  VProgressCircular,
  VList,
  VListItem,
  VListItemContent,
  VListItemTitle,
  VListItemSubtitle,
  VListItemAvatar,
  VListItemAction,
  VIcon,
  VChip,
  VBtn,
} from "vuetify/lib";
import { apiConfig } from "@/config/api.config";
import BranchDataService from "@/services/BranchDataService";
import DeptDataService from "@/services/DeptDataService";
import moment from "moment"; // <-- Add this import

export default {
  name: "ActiveUsers",
  components: {
    VAlert,
    VProgressCircular,
    VList,
    VListItem,
    VListItemContent,
    VListItemTitle,
    VListItemSubtitle,
    VListItemAvatar,
    VListItemAction,
    VIcon,
    VChip,
    VBtn,
  },
  data() {
    return {
      loading: false,
      users: [],
      error: null,
      branches: [],
      departments: [],
    };
  },
  methods: {
    async fetchActiveUsers() {
      this.loading = true;
      this.error = null;
      try {
        let url = `${apiConfig.baseURL}/management/active-users`;
        url = url.replace(/([^:]\/)\/+/g, "$1");
        const res = await fetch(url);
        if (!res.ok) throw new Error("Network error");
        this.users = await res.json();
      } catch (e) {
        this.error = "Failed to load active users.";
      } finally {
        this.loading = false;
      }
    },
    formatDateTime(dt) {
      if (!dt) return "";
      return moment(dt).format("DD/MM/YYYY HH:mm:ss");
    },
    getRoleNames(roles) {
      // roles can be array of objects or strings
      if (!roles) return [];
      if (Array.isArray(roles)) {
        return roles.map((r) => r.roleCode || r.name || r);
      }
      // If roles is a string or single object
      if (typeof roles === "string") return [roles];
      if (typeof roles === "object" && roles.roleCode) return [roles.roleCode];
      return [];
    },
    getBranchNames(branches) {
      if (!branches) return [];
      // If array of objects, extract name; if array of ids, lookup name
      if (Array.isArray(branches)) {
        return branches
          .map((b) => {
            if (typeof b === "object" && b.name) return b.name;
            const branchObj = this.branches.find(
              (br) => br.id === b || br.id === Number(b)
            );
            return branchObj ? branchObj.name : b;
          })
          .filter(Boolean);
      }
      // Single object or id
      if (typeof branches === "object" && branches.name) return [branches.name];
      const branchObj = this.branches.find(
        (br) => br.id === branches || br.id === Number(branches)
      );
      return branchObj ? [branchObj.name] : [branches];
    },
    getDeptNames(depts) {
      // depts can be array of ids or objects
      if (!depts) return [];
      if (!Array.isArray(depts)) depts = [depts];
      return depts
        .map((deptId) => {
          const deptObj = this.departments.find(
            (d) => d.id === deptId || d.id === Number(deptId)
          );
          return deptObj ? deptObj.name : deptId;
        })
        .filter(Boolean);
    },
    async fetchBranches() {
      try {
        const response = await BranchDataService.getAll();
        this.branches = response.data;
      } catch (e) {
        // ignore branch fetch error
      }
    },
    async fetchDepartments() {
      try {
        const response = await DeptDataService.getAll();
        this.departments = response.data;
      } catch (e) {
        // ignore dept fetch error
      }
    },
  },
  created() {
    this.fetchBranches();
    this.fetchDepartments();
    this.fetchActiveUsers();
  },
};
</script>

<style scoped></style>
